import { t } from 'i18next';
import { isEqual } from 'lodash';
import { Outlet } from 'react-router-dom';
import { Breadcrumb } from '../../breadcrumbs/models/Breadcrumb';
import { Report as ReportBreadcrumb } from '../../breadcrumbs/models/Report';
import { BaseController, BaseState } from '../../common/controllers/BaseController';
import { WithRouterProps, withRouter } from '../../common/higher-order-components/with-router';
import { CacheTTL } from '../../common/services/CacheService';
import { TestValueWrapper } from '../../common/utils/TestValueWrapper';
import { UrlHelper } from '../../common/utils/UrlHelper';
import { bind } from '../../common/utils/decorators';
import { ReportExportDrawer } from '../../exports/triggers/drawers/ReportExportDrawer';
import { ReportExportState } from '../../exports/triggers/states/ReportExportState';
import { SecondaryNav } from '../../layout/components/SecondaryNav';
import { PATHS, REPORT_TABS, Route } from '../../routing/routes';
import { ReportLoader, ReportLoaderChildren } from '../common/components/ReportLoader';
import { ReportOutletContextType } from '../common/contexts/ReportOutletContext';
import { ReportBreadcrumbMapper } from '../common/mappers/ReportBreadcrumbMapper';
import { Report, ReportDone } from '../common/models/Report';
import { ReportRepository } from '../common/services/ReportRepository';

type ReportNavParams = { reportId: string; slotId: string; projectId: string };

type ReportNavHeader = string | undefined;

type Props = WithRouterProps & {};

type State = BaseState & {
  isLoadingNavContext: boolean;
  isLoadingReport: boolean;
};

class Controller extends BaseController<Props, State> {
  private readonly reportRepository = new ReportRepository();
  private readonly breadcrumbMapper = new ReportBreadcrumbMapper();

  private reportId: number;

  private navHeader: ReportNavHeader | undefined;
  private nestedTabs: Route[] | undefined;

  private reportBreadcrumb: ReportBreadcrumb | null | undefined;

  private exportState: ReportExportState;

  constructor(props: Props, context: unknown) {
    super(props, context);

    ({ reportId: this.reportId } = this.parseNumericIds(['reportId']));

    this.exportState = ReportExportState.getInstance();

    this.updateReportBreadcrumb();

    // Manually set the state instead of using `getBaseState`
    // When `getBaseState` is called in `BaseController` we cannot use `this.reportId`
    // or `this.reportBreadcrumb`, and updating the state in `componentDidMount` with
    // values we already know here means one pointless re-render.
    // One alternative would be to read `this.reportId` and `this.reportBreadcrumb`
    // again in `this.getBaseState`, but it's a code duplication that can be avoided
    this.state = {
      ...this.state,
      isLoadingNavContext: !this.reportBreadcrumb,
      isLoadingReport: true,
    };
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    super.componentDidUpdate(prevProps, prevState);

    // Handle report change from the report version selector
    // Do not refresh the view on updates to child paths (i.e. switching tabs)
    const { reportId } = this.parseNumericIds(['reportId']);
    if (reportId === this.reportId) return;

    this.reportId = reportId;
    this.updateReportBreadcrumb();
    this.setState({ isLoadingNavContext: !this.reportBreadcrumb, isLoadingReport: true });

    // The report has changed: check if we are on any nested path (regardless of the report type).
    // Nested content of one report will likely not exist in another report (a Key belongs to one report,
    // Issue reportId_1/issueNumber_1 will not be the same Issue as reportId_2/issueNumber_1...).
    // To avoid inconsistent queries and display, redirect to the top level path of the current path
    const parentTab = this.getPathParentTab(REPORT_TABS());
    if (parentTab) {
      this.redirect(UrlHelper.generatePath(parentTab.path, { reportId: this.reportId.toString() }));
      return;
    }
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.exportState.reset();
    this.reportRepository.cancelAllRequests();
  }

  @bind
  getBreadcrumbs(): Breadcrumb[] {
    const { slotId, reportId, projectId } = this.getNavParams();

    return this.breadcrumbFactory.getReportBreadcrumbs({
      reportId,
      reportLabel: this.reportBreadcrumb?.getLabel() || reportId,
      slotId,
      slotLabel: this.reportBreadcrumb?.getSlot()?.getLabel() || slotId,
      projectId,
      projectLabel: this.reportBreadcrumb?.getSlot()?.getProject()?.getLabel() || projectId,
      nestedTabs: this.getNestedTabs(),
    });
  }

  getNavParams(): ReportNavParams {
    return {
      reportId: this.reportBreadcrumb?.getId()?.toString() || this.reportId.toString() || '',
      slotId: this.reportBreadcrumb?.getSlot()?.getId()?.toString() || '',
      projectId: this.reportBreadcrumb?.getSlot()?.getProject()?.getId()?.toString() || '',
    };
  }

  getNestedTabs(): Route[] {
    return UrlHelper.generatePaths(REPORT_TABS(this.reportBreadcrumb?.getTraceType()), this.getNavParams());
  }

  getNavHeader(): ReportNavHeader {
    return this.reportBreadcrumb?.getSlot()?.getLabel();
  }

  render() {
    return this.renderBase(
      <SecondaryNav
        title={this.navHeader ?? ''}
        rightSection={
          // FIXME: handle out-of-lifecycle signal updates in tests
          TestValueWrapper.wrap(<ReportExportDrawer onNotifications={this.showMessages} onError={this.handleError} />, null)
        }
        getBreadcrumbs={this.getBreadcrumbs}
        tabs={this.nestedTabs}
        loading={this.state.isLoadingNavContext}
      >
        <ReportLoader reportId={this.reportId} loader={this.reportLoader}>
          {this.reportOutlet}
        </ReportLoader>
      </SecondaryNav>
    );
  }

  /**
   * Retrieves a report, updates the breadcrumb cache with it and redirects in
   * case the tab we are trying to access is not supported by the report.
   *
   * Defined as a class property rather than method to avoid having to
   * use an arrow function in `render`, which would cause `ReportLoader`
   * to trigger every time this controller is re-rendered.
   *
   * @param reportId the ID of the report to load
   * @returns the report or `null` in case of error or invalid path access
   */
  private reportLoader = async (reportId: number) => {
    try {
      const report = await this.reportRepository.getReport(reportId);

      const reportBreadcrumb = this.breadcrumbMapper.mapToBreadcrumb(report);

      // Store the report breadcrumb with a long TTL to prevent flickering
      // of the `SecondaryNav` as much as possible. This breadcrumb will always
      // be updated as soon as any tab of any report is displayed and its content will be refreshed
      this.breadcrumbCachingService.saveReport(reportBreadcrumb, CacheTTL.ONE_YEAR);

      // Project breadcrumb is not used in ReportController, but it is updated to be ready for other controllers
      const slotProject = reportBreadcrumb.getSlot()?.getProject();
      if (slotProject) {
        this.breadcrumbCachingService.saveProject(slotProject);
      }

      this.updateReportBreadcrumb();

      if (report instanceof ReportDone) {
        this.exportState.setState(report);
      } else {
        this.exportState.reset();
      }

      this.setState({ isLoadingReport: false, isLoadingNavContext: false });

      const pathIsValid = this.validatePathAccess(report);
      return pathIsValid ? report : null;
    } catch (e: unknown) {
      this.handleError(e, { invalidUrl: t('nonExistingReport') });
      return null;
    }
  };

  /**
   * Renderer function required by `ReportLoader` to display the current
   * tab and pass down the current report to nested controllers.
   *
   * Defined as a class property rather than method to avoid having to
   * use an arrow function in `render`, which would cause `ReportLoader`
   * to trigger every time this controller is re-rendered.
   */
  private reportOutlet: ReportLoaderChildren = (report) => {
    const context: ReportOutletContextType = { report };
    return <Outlet context={context} />;
  };

  /**
   * Validates that the path we are trying to access is supported by the current report.
   * Validation happens in two steps:
   * - check that the path exists in the `REPORT_TABS` for the current `traceType`:
   *   - for example, prevents accessing "Handshakes" on a non-network report
   *   - if validation fails, redirect to the "Issues" tab, which is supported for all
   * - if a report is not done (pending or failed), prevents access to nested tabs
   *   - for example, prevents accessing one "key", which will not exist on not-done reports
   *   - if validation fails, redirect to the top-level tab of the nested tab (for example, "Keys")
   *
   * @param report the current report
   * @returns `true` if the path can be loaded, false otherwise
   */
  private validatePathAccess(report: Report): boolean {
    const reportTabs = REPORT_TABS(report.getTraceType());
    const currentPath = this.props.location.pathname;

    // Prevent direct access to paths that are not supported for the current report
    const availablePaths = UrlHelper.getFlatPaths(reportTabs);
    const pathIsAvailable = availablePaths.find((path) => UrlHelper.matchPath(path, currentPath));
    if (!pathIsAvailable) {
      this.redirect(UrlHelper.generatePath(PATHS.REPORT, { reportId: this.reportId.toString() }));
      return false;
    }
    // The path we are accessing is compatible with the current report.
    // However, if the report is `ReportPending` or `ReportFailed` it would not make sense
    // to load a nestedPath (e.g. a single key, or a single issue) because they would
    // not be available yet. In this case, redirect to the top-level path of the nestedPath.
    if (!(report instanceof ReportDone)) {
      const parentTab = this.getPathParentTab(reportTabs);
      if (parentTab) {
        this.redirect(UrlHelper.generatePath(parentTab.path, { reportId: this.reportId.toString() }));
        return false;
      }
    }

    return true;
  }

  private getPathParentTab(tabs: Route[]): Route | null {
    const currentPath = this.props.location.pathname;
    for (const tab of tabs) {
      const isNestedTab = (tab.nestedPaths || []).find((path) => UrlHelper.matchPath(path, currentPath));
      if (isNestedTab) {
        return tab;
      }
    }
    return null;
  }

  private updateReportBreadcrumb() {
    const cacheBreadcrumb = this.breadcrumbCachingService.getReport(this.reportId);

    // Prevent changes to the object reference of internal
    // properties in case the cache has not changed.
    // This avoids meaningless re-renders on the `SecondaryNav`
    if (isEqual(this.reportBreadcrumb, cacheBreadcrumb)) return;

    this.reportBreadcrumb = cacheBreadcrumb;
    this.navHeader = this.getNavHeader();
    this.nestedTabs = this.getNestedTabs();
  }
}

export const ReportController = withRouter(Controller);
